// /lib/chart-helpers/custom-overlays/tradeHistoryLineOverlay.ts

import { defaultTradeOverlayTheme } from "../trade-overlay/theme";

import { klinecharts } from "../klinecharts-client";

if (typeof window !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    .klinecharts-overlay-point {
      display: none !important;
    }
    .klinecharts-overlay .point {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
}

export interface TradeHistoryData {
  ticket: string;
  direction: "BUY" | "SELL";
  lots: number;
  openTime: number;
  closeTime: number;
  openPrice: number;
  closePrice: number;
  profit: number;
  symbol: string;
  digits: number;
  isProfit: boolean;
  /** Pre-formatted profit / pips display ("+12.40", "-3.5p", ...) */
  profitDisplay: string;
}

/**
 * Profit-state palette: blue for profitable trades, red for losing trades.
 * Matches the broader app convention (`text-blue-400` / `text-red-400`)
 * used in the watchlist, open positions and history rows.
 */
const PROFIT_COLOR = defaultTradeOverlayTheme.buyColor; // "#2196F3"
const LOSS_COLOR = defaultTradeOverlayTheme.pnlNegativeColor; // "#F44336"

const HEAD_WIDTH = 6;
const HEAD_HEIGHT = 6;
const BODY_WIDTH = 3;
const BODY_HEIGHT = 10;

/**
 * Filled-arrow polygon with its tip pinned at (px, py).
 * `direction === "up"`: tip points up, body extends downward (below the price).
 * `direction === "down"`: tip points down, body extends upward (above the price).
 */
function buildArrowPoints(
  px: number,
  py: number,
  direction: "up" | "down"
): Array<{ x: number; y: number }> {
  if (direction === "up") {
    return [
      { x: px, y: py },
      { x: px + HEAD_WIDTH, y: py + HEAD_HEIGHT },
      { x: px + BODY_WIDTH, y: py + HEAD_HEIGHT },
      { x: px + BODY_WIDTH, y: py + HEAD_HEIGHT + BODY_HEIGHT },
      { x: px - BODY_WIDTH, y: py + HEAD_HEIGHT + BODY_HEIGHT },
      { x: px - BODY_WIDTH, y: py + HEAD_HEIGHT },
      { x: px - HEAD_WIDTH, y: py + HEAD_HEIGHT },
    ];
  }

  return [
    { x: px, y: py },
    { x: px + HEAD_WIDTH, y: py - HEAD_HEIGHT },
    { x: px + BODY_WIDTH, y: py - HEAD_HEIGHT },
    { x: px + BODY_WIDTH, y: py - HEAD_HEIGHT - BODY_HEIGHT },
    { x: px - BODY_WIDTH, y: py - HEAD_HEIGHT - BODY_HEIGHT },
    { x: px - BODY_WIDTH, y: py - HEAD_HEIGHT },
    { x: px - HEAD_WIDTH, y: py - HEAD_HEIGHT },
  ];
}

/**
 * Returns the entry/exit arrow directions for a given trade direction.
 * - BUY: entry arrow points up (deal in below price), exit arrow points down (deal out above price).
 * - SELL: entry arrow points down, exit arrow points up.
 */
function getArrowDirections(tradeDirection: "BUY" | "SELL"): {
  entry: "up" | "down";
  exit: "up" | "down";
} {
  if (tradeDirection === "BUY") {
    return { entry: "up", exit: "down" };
  }
  return { entry: "down", exit: "up" };
}

const createTradeHistoryFigures = (params: any) => {
  const { coordinates, overlay } = params;

  if (!coordinates || coordinates.length < 2) return [];

  const tradeData: TradeHistoryData | undefined = overlay?.extendData;
  if (!tradeData) return [];

  const [startPoint, endPoint] = coordinates;
  if (
    !startPoint ||
    !endPoint ||
    typeof startPoint.x !== "number" ||
    typeof startPoint.y !== "number" ||
    typeof endPoint.x !== "number" ||
    typeof endPoint.y !== "number"
  ) {
    return [];
  }

  const lineColor = tradeData.isProfit ? PROFIT_COLOR : LOSS_COLOR;
  const directions = getArrowDirections(tradeData.direction);

  const figures: any[] = [];

  figures.push({
    key: "tradeLine",
    type: "line",
    attrs: {
      coordinates: [
        { x: startPoint.x, y: startPoint.y },
        { x: endPoint.x, y: endPoint.y },
      ],
    },
    styles: {
      style: "dashed",
      dashedValue: [4, 4],
      color: lineColor,
      size: 1.5,
    },
  });

  figures.push({
    key: "entryArrow",
    type: "polygon",
    attrs: {
      coordinates: buildArrowPoints(
        startPoint.x,
        startPoint.y,
        directions.entry
      ),
    },
    styles: {
      style: "stroke_fill",
      color: lineColor,
      borderColor: "#FFFFFF",
      borderSize: 1,
    },
  });

  figures.push({
    key: "exitArrow",
    type: "polygon",
    attrs: {
      coordinates: buildArrowPoints(
        endPoint.x,
        endPoint.y,
        directions.exit
      ),
    },
    styles: {
      style: "stroke_fill",
      color: lineColor,
      borderColor: "#FFFFFF",
      borderSize: 1,
    },
  });

  const profitText = tradeData.profitDisplay;
  if (profitText) {
    const exitVerticalOffset =
      directions.exit === "down" ? HEAD_HEIGHT + BODY_HEIGHT + 2 : -(HEAD_HEIGHT + BODY_HEIGHT + 2);

    figures.push({
      key: "profitBadge",
      type: "text",
      attrs: {
        x: endPoint.x + HEAD_WIDTH + 4,
        y: endPoint.y + exitVerticalOffset,
        text: profitText,
        align: "left",
        baseline: "middle",
      },
      styles: {
        style: "fill",
        color: "#FFFFFF",
        size: 10,
        family: "Arial",
        weight: "bold",
        backgroundColor: lineColor,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        borderRadius: 3,
        borderStyle: "solid",
        borderColor: lineColor,
        borderSize: 1,
      },
      ignoreEvent: false,
    });
  }

  return figures;
};

export const tradeHistoryLineOverlay = {
  name: "customTradeHistoryLine",
  totalStep: 2,
  lock: true,
  visible: true,
  /** Below live trade overlays (zLevel: 10) so open positions stay dominant. */
  zLevel: 4,
  needDefaultPointFigure: false,
  needDefaultXAxisFigure: false,
  needDefaultYAxisFigure: false,
  mode: "normal" as const,
  modeSensitivity: 8,

  createPointFigures: (params: any) => {
    return createTradeHistoryFigures(params);
  },

  onClick: (event: any) => {
    const { overlay, figureKey } = event;
    const tradeData: TradeHistoryData | undefined = overlay?.extendData;
    if (!tradeData) return false;

    if (figureKey === "profitBadge" && overlay.onTradeClick) {
      overlay.onTradeClick(tradeData);
      return false;
    }

    if (overlay.onTradeClick) {
      overlay.onTradeClick(tradeData);
    }

    return false;
  },

  onDoubleClick: (event: any) => {
    const tradeData: TradeHistoryData | undefined = event.overlay?.extendData;
    if (tradeData && event.overlay.onTradeDoubleClick) {
      event.overlay.onTradeDoubleClick(tradeData);
    }
    return true;
  },

  onPressedMoveStart: () => true,
  onPressedMoving: () => true,
  onPressedMoveEnd: () => true,
};

export const registerTradeHistoryLineOverlay = () => {
  if (!klinecharts) return;
  try {
    klinecharts.registerOverlay(tradeHistoryLineOverlay);
  } catch (error) {
    console.error("Failed to register trade history line overlay:", error);
  }
};

export const createTradeHistoryLineOverlay = (
  chartInstance: any,
  tradeData: TradeHistoryData,
  onTradeClick?: (trade: TradeHistoryData) => void,
  onTradeDoubleClick?: (trade: TradeHistoryData) => void
): string | null => {
  if (!chartInstance) return null;

  const overlayConfig = {
    name: "customTradeHistoryLine",
    lock: true,
    points: [
      {
        timestamp: tradeData.openTime,
        value: tradeData.openPrice,
      },
      {
        timestamp: tradeData.closeTime,
        value: tradeData.closePrice,
      },
    ],
    extendData: tradeData,
  };

  const overlayId = chartInstance.createOverlay(overlayConfig);

  if (overlayId) {
    const overlay = chartInstance.getOverlayById(overlayId);
    if (overlay) {
      overlay.onTradeClick = onTradeClick;
      overlay.onTradeDoubleClick = onTradeDoubleClick;
    }
  }

  return overlayId;
};
